"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type TrainPlanRow = {
  id: string
  planNumber: string
  name: string
  status: string
  locomotiveId: string
  totalWeightTonnes: number | null
  totalLengthMetres: number | null
  departureDate: string | null
  createdAt: string
}

type TrainPlansResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapTrainPlanRow(item: Record<string, unknown>): TrainPlanRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    planNumber: typeof item.plan_number === 'string' ? item.plan_number : '',
    name: typeof item.name === 'string' ? item.name : '',
    status: typeof item.status === 'string' ? item.status : 'draft',
    locomotiveId: typeof item.locomotive_id === 'string' ? item.locomotive_id : '',
    totalWeightTonnes: typeof item.total_weight_tonnes === 'number' ? item.total_weight_tonnes : null,
    totalLengthMetres: typeof item.total_length_metres === 'number' ? item.total_length_metres : null,
    departureDate: typeof item.departure_date === 'string' ? item.departure_date : null,
    createdAt: typeof item.created_at === 'string' ? item.created_at : '',
  }
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  validated: 'Validated',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
}

const STATUS_CLASS: Record<string, string> = {
  draft: 'text-muted-foreground',
  validated: 'text-blue-600',
  confirmed: 'text-green-600',
  cancelled: 'text-red-600',
}

export default function TrainPlansPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'train-planning-list',
  })
  const [rows, setRows] = React.useState<TrainPlanRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()
  const router = useRouter()

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'status',
      label: t('train_planning.plans.filters.status', 'Status'),
      type: 'select' as const,
      options: Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
    },
  ], [t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const status = filterValues.status
    if (typeof status === 'string' && status.trim()) params.set('status', status)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: TrainPlansResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<TrainPlansResponse>(
          `/api/train_planning/train-plans?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('train_planning.plans.error.load', 'Failed to load train plans')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapTrainPlanRow).filter((row): row is TrainPlanRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('train_planning.plans.error.load', 'Failed to load train plans')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleDelete = React.useCallback(async (plan: TrainPlanRow) => {
    if (!plan.id) return
    const confirmed = await confirm({
      title: t('train_planning.plans.deleteConfirm', 'Delete plan {{planNumber}}?', { planNumber: plan.planNumber }),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/train_planning/train-plans?id=${encodeURIComponent(plan.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('train_planning.plans.deleteError', 'Failed to delete train plan') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== plan.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('train_planning.plans.deleteSuccess', 'Train plan deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('train_planning.plans.deleteError', 'Failed to delete train plan')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<TrainPlanRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'planNumber',
        header: t('train_planning.plans.columns.planNumber', 'Plan Number'),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.planNumber}</span>,
      },
      {
        accessorKey: 'name',
        header: t('train_planning.plans.columns.name', 'Name'),
        cell: ({ row }) => row.original.name || noValue,
      },
      {
        accessorKey: 'status',
        header: t('train_planning.plans.columns.status', 'Status'),
        cell: ({ row }) => {
          const statusClass = STATUS_CLASS[row.original.status] ?? ''
          return (
            <span className={statusClass}>
              {STATUS_LABELS[row.original.status] ?? row.original.status}
            </span>
          )
        },
      },
      {
        accessorKey: 'locomotiveId',
        header: t('train_planning.plans.columns.locomotiveId', 'Locomotive'),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.locomotiveId ? row.original.locomotiveId.slice(0, 8) + '…' : noValue}
          </span>
        ),
      },
      {
        accessorKey: 'totalWeightTonnes',
        header: t('train_planning.plans.columns.totalWeightTonnes', 'Weight (t)'),
        cell: ({ row }) => row.original.totalWeightTonnes !== null ? `${row.original.totalWeightTonnes}` : noValue,
      },
      {
        accessorKey: 'totalLengthMetres',
        header: t('train_planning.plans.columns.totalLengthMetres', 'Length (m)'),
        cell: ({ row }) => row.original.totalLengthMetres !== null ? `${row.original.totalLengthMetres}` : noValue,
      },
      {
        accessorKey: 'departureDate',
        header: t('train_planning.plans.columns.departureDate', 'Departure'),
        cell: ({ row }) => row.original.departureDate ?? noValue,
      },
    ]
  }, [t])

  return (
    <Page>
      <PageBody>
        <DataTable<TrainPlanRow>
          title={t('train_planning.plans.title', 'Train Plans')}
          refreshButton={{
            label: t('train_planning.plans.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <Button asChild>
              <Link href="/backend/train-planning/create">
                {t('train_planning.plans.actions.create', 'New Plan')}
              </Link>
            </Button>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('train_planning.plans.searchPlaceholder', 'Search by plan number or name...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'train_planning.plans.list' }}
          onRowClick={(row) => router.push(`/backend/train-planning/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('train_planning.plans.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
